import { put, select, takeLatest, all, call } from "redux-saga/effects";
import { addAddonToCart, fetchAddOnInfo, fetchAddOnInfoNew } from "../../../api/common/addOn";
import {
  requestToFetchAddOnInfoAction,
  successToFetchAddOnInfoAction,
  failToFetchAddOnInfoAction,
  addOnModalVisibility,
  addAddonToCartAction,
  successAddonToCartAction,
  failAddonToCartAction,
} from "../../slices/common/addOn";
import { resetMultiProductDateRange, setDeliveryDetailsAction } from "../../slices/common/shippingDateAndSlotSelection";
import { setSelectedDateFromAddToCart } from "../../slices/pdpSlice";
import isEmpty from "lodash.isempty";
import { BUY_NOW } from "../../../constants/common/common";
import { redirectToCheckout } from "../../../utils/common";
import {
  addAddonToCartPDPAction,
  pdpAddOnModalVisibility,
  requestToFetchPdpAddOnInfoAction,
  requestToFetchPdpAddOnInfoNewAction,
  resetPdpAddOnState,
  resetPdpSelectedAddons,
  setAutoScroll,
  successAddonToCartPDPAction,
  successToFetchPdpAddOnInfoAction,
} from "../../slices/pdpAddonSlice";
import { initiateFusCall } from "./index";
import { fetchCartAmountSummarySaga, fetchCompleteCartDetailsOnDeliveryPage } from "../deliveryAddressSaga/index.js";

/**
 *
 * @param {*} action
 */
function* getAddOnInfoSaga({ payload }) {
  const isBuyNowClicked = yield select((state) => state.pdp?.checkBuyNowOrProceedToPay);

  try {
    const response = yield fetchAddOnInfo(payload);
    yield put(successToFetchAddOnInfoAction(response));
    if (isEmpty(response?.addonsList?.[0]?.products)) {
      if (isBuyNowClicked !== BUY_NOW) {
        yield put(setDeliveryDetailsAction({}));
      } else {
        const action = yield call(redirectToCheckout);
        if (action) {
          yield put(action);
        }
      }
    } else {
      yield put(addOnModalVisibility(true));
    }
  } catch (error) {
    yield put(failToFetchAddOnInfoAction(error));
    yield put(addOnModalVisibility(false));
  }
}

/**
 *
 * @param {*} action
 */
function* addAddonToCartSaga({ payload }) {
  const param = typeof payload === "string" ? payload : payload?.param;
  const isBuyNowClicked = yield select((state) => state.pdp?.checkBuyNowOrProceedToPay);
  try {
    yield call(addAddonToCart, param);
    yield put(setDeliveryDetailsAction({}));
    yield put(resetMultiProductDateRange());
    if (!(isBuyNowClicked === BUY_NOW)) {
      yield put(addOnModalVisibility(false));
    }
    yield put(resetPdpAddOnState());
    yield put(successAddonToCartAction());
    yield call(fetchCompleteCartDetailsOnDeliveryPage);
    yield call(fetchCartAmountSummarySaga);
  } catch (error) {
    yield put(failAddonToCartAction());
  }
}

/**
 *
 * @param {*} action
 */
function* fetchPdpAddons({ payload }) {
  yield put(resetPdpAddOnState());
  const response = yield fetchAddOnInfo(payload);
  yield put(
    successToFetchPdpAddOnInfoAction({
      masterProductId: payload?.addProductId,
      ...response,
    }),
  );
}

/**
 *
 * @param {*} action
 */
function* fetchPdpAddonsNew({ payload }) {
  yield put(resetPdpAddOnState());
  const response = yield fetchAddOnInfoNew({ ...payload });
  yield put(
    successToFetchPdpAddOnInfoAction({
      masterProductId: payload?.addProductId,
      ...response,
    }),
  );
}

/**
 * Completes the PDP add-on continue flow. Calls `addAddonsToCart-rj` only when at
 * least one addon is selected; skip/empty selection still closes the modal and
 * finishes the cart flow without that API.
 *
 * @param {Object} action
 * @param {Object} action.payload
 * @param {string} action.payload.param - Form-encoded request body.
 */
function* addPdpAddonToCartSaga({ payload }) {
  try {
    const { param } = payload;
    const hasSelectedAddons = typeof param === "string" && param.includes("addonProductId");

    const [isBuyNowClicked, deliveryDates, blockedDates] = yield all([
      select((state) => state.pdp?.checkBuyNowOrProceedToPay),
      select((state) => state?.shippingDateAndSlotSelection?.deliveryDates?.deliveryDates),
      select((state) => state?.shippingDateAndSlotSelection?.blockedDates?.deliveryBlockDates),
    ]);
    const availableDates = Array.isArray(deliveryDates)
      ? deliveryDates.filter((date) => !blockedDates?.includes(date))
      : [];
    const firstDeliveryDate = availableDates[0] || deliveryDates?.[0] || "";

    if (hasSelectedAddons) {
      yield call(addAddonToCart, param);
    }

    yield all([
      put(setDeliveryDetailsAction(firstDeliveryDate ? { selectedDate: firstDeliveryDate } : {})),
      put(setSelectedDateFromAddToCart(firstDeliveryDate || "")),
      put(setAutoScroll(false)),
      put(resetMultiProductDateRange()),
      put(pdpAddOnModalVisibility(false)),
      put(successAddonToCartPDPAction()),
    ]);
    yield put(resetPdpSelectedAddons());

    if (isBuyNowClicked === BUY_NOW) {
      // Handle Buy Now flow
      const action = yield call(redirectToCheckout);
      if (action) yield put(action);
    } else {
      yield call(initiateFusCall);
    }
  } catch (error) {
    yield put(failAddonToCartAction());
  }
}

/**
 * Description placeholder
 *
 * @export
 * @returns {{}}
 */
export function* watcherAddOnSaga() {
  yield takeLatest(requestToFetchAddOnInfoAction.type, getAddOnInfoSaga);
  yield takeLatest(addAddonToCartAction.type, addAddonToCartSaga);
  yield takeLatest(requestToFetchPdpAddOnInfoAction.type, fetchPdpAddons);
  yield takeLatest(requestToFetchPdpAddOnInfoNewAction.type, fetchPdpAddonsNew);
  yield takeLatest(addAddonToCartPDPAction.type, addPdpAddonToCartSaga);
}
